import { useState } from 'react'
import { connect, useSelector, type ConnectedProps } from 'react-redux'
import type { AnyAction } from 'redux'
import type { ThunkDispatch } from 'redux-thunk'
import type { StoreStateType } from 'index'
import { Helmet } from 'react-helmet'
import { Agreements, Text, Button, Spacer, Hr, Container, RoadieUIThemeRealm } from 'roadie-ui'
import Page from 'rui/widgets/Page'
import Thin from 'components/layout/Thin'
import Panel, { PanelHeading, PanelSubheading } from 'components/layout/Panel'
import Step from 'components/misc/Step'
import { disclosuresSelector, profileSelector } from 'selectors'
import { toLegalAgreementSubmission, type AgreementCompletedArgs, type LegalAgreement } from 'data/agreements'
import { getProfile } from 'actions/profile'
import { createLegalAgreements } from 'util/server/mainApi'
import { signingApi } from 'util/server/signingApi'
import { sendNativeMessage } from 'util/native'
import { formatRegistrationStep } from 'util/registrationFlow'
import { useLocalization, LocalizedText } from 'localization'

const connector = connect(disclosuresSelector)
type DisclosuresProps = Omit<ConnectedProps<typeof connector>, 'dispatch'> & {
  dispatch: ThunkDispatch<StoreStateType, void, AnyAction>
}

const Disclosures = ({ disclosureDocuments, isWebview, mediaSize, dispatch }: DisclosuresProps) => {
  const { t } = useLocalization()
  const { profile } = useSelector(profileSelector)
  const registrationStep = formatRegistrationStep('DISCLOSURES', profile)
  const [showAgreements, setShowAgreements] = useState(false)
  const agreements: LegalAgreement[] = disclosureDocuments?.documents ?? []
  const isWebOrMobile = isWebview || mediaSize === 'mobile'

  const fetchAgreementHtml = (agreement: Pick<LegalAgreement, 'version_id'>) =>
    signingApi.fetchDocumentHtml(agreement.version_id)

  const handleAgreementCompleted = async (args: AgreementCompletedArgs) => {
    await createLegalAgreements([toLegalAgreementSubmission(args)])
  }

  const handleViewDisclosures = () => {
    setShowAgreements(true)
  }

  const handleAllAgreementsCompleted = async () => {
    // TODO: Check with mobile team if this is the correct message to send (update to DISCLOSURES_ACCEPTED?)
    // May need to update to only fire if not in sign up flow
    sendNativeMessage({ type: 'TERMS_ACCEPTED' })
    await dispatch(getProfile())
  }

  return (
    <Page>
      <Helmet>
        <title>{t('dweb_disclosures_disclosure_authorization')}</title>
      </Helmet>
      <Thin>
        {showAgreements ? (
          <RoadieUIThemeRealm mode="light">
            <Container.Panel $paddingMedia={{ mobile: '0' }}>
              <Container.Base
                $minHeight="60rem"
                $height={isWebview ? '100vh' : '75vh'}
                $padding={isWebOrMobile ? '2.4rem 2.4rem 2.4rem 2.4rem' : undefined}
              >
                <Agreements
                  agreements={agreements}
                  fetchAgreementHtml={fetchAgreementHtml}
                  onAgreementCompleted={handleAgreementCompleted}
                  onAllAgreementsCompleted={handleAllAgreementsCompleted}
                />
              </Container.Base>
            </Container.Panel>
          </RoadieUIThemeRealm>
        ) : (
          <Panel>
            <PanelHeading>
              <LocalizedText translationKey="dweb_disclosures_disclosure_authorization" />
            </PanelHeading>
            <PanelSubheading>
              <LocalizedText translationKey="dweb_disclosures_we_will_guide_through_following_disclosures_where_youll" />
            </PanelSubheading>

            <Spacer $height="3.2rem" />

            {agreements.map((agreement, index) => (
              <div key={agreement.document_type}>
                {index > 0 && <Hr $paddingTop="2.4rem" $paddingBottom="2.4rem" />}
                <Text.Body1 $textAlign="start" $fontWeight="bold" $color="neutral-black">
                  {agreement.title}
                </Text.Body1>
              </div>
            ))}

            <Spacer $height="4rem" />

            <Button.Primary $width="100%" $height="5rem" onClick={handleViewDisclosures}>
              <LocalizedText translationKey="dweb_disclosures_view_disclosures" />
            </Button.Primary>

            <Spacer $height="1rem" />

            {registrationStep && <Step>{registrationStep}</Step>}
          </Panel>
        )}
      </Thin>
    </Page>
  )
}

export default connector(Disclosures)
